import { useRef, type KeyboardEvent } from "react";

export type AgentModelTab =
  | "summary"
  | "model"
  | "assumptions"
  | "guidance"
  | "documents"
  | "run-details";

const AGENT_MODEL_TABS: {
  id: AgentModelTab;
  label: string;
}[] = [
  { id: "summary", label: "Summary" },
  { id: "model", label: "Model" },
  { id: "assumptions", label: "Assumptions Review" },
  { id: "guidance", label: "Guidance" },
  { id: "documents", label: "Documents" },
  { id: "run-details", label: "Run details" },
];

export function agentModelTab(value: string | null): AgentModelTab {
  return AGENT_MODEL_TABS.some((tab) => tab.id === value)
    ? (value as AgentModelTab)
    : "summary";
}

export function nextAgentModelTab(
  current: AgentModelTab,
  key: string,
  tabs: readonly AgentModelTab[] = AGENT_MODEL_TABS.map((tab) => tab.id),
): AgentModelTab | null {
  const index = tabs.indexOf(current);
  if (index < 0 || tabs.length === 0) return null;
  if (key === "Home") return tabs[0] ?? null;
  if (key === "End") return tabs.at(-1) ?? null;
  if (key === "ArrowRight" || key === "ArrowDown") {
    return tabs[(index + 1) % tabs.length] ?? null;
  }
  if (key === "ArrowLeft" || key === "ArrowUp") {
    return tabs[(index - 1 + tabs.length) % tabs.length] ?? null;
  }
  return null;
}

export default function AgentModelTabs({
  value,
  onChange,
  tabs = AGENT_MODEL_TABS.map((tab) => tab.id),
}: {
  value: AgentModelTab;
  onChange: (value: AgentModelTab) => void;
  tabs?: readonly AgentModelTab[];
}) {
  const visibleTabs = AGENT_MODEL_TABS.filter((tab) => tabs.includes(tab.id));
  const buttons = useRef<Record<AgentModelTab, HTMLButtonElement | null>>({
    summary: null,
    model: null,
    assumptions: null,
    guidance: null,
    documents: null,
    "run-details": null,
  });

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    current: AgentModelTab,
  ) => {
    const next = nextAgentModelTab(
      current,
      event.key,
      visibleTabs.map((tab) => tab.id),
    );
    if (!next) return;
    event.preventDefault();
    onChange(next);
    buttons.current[next]?.focus();
  };

  return (
    <div className="agent-model-tabs" role="tablist" aria-label="Model views">
      {visibleTabs.map((tab) => (
        <button
          key={tab.id}
          ref={(element) => {
            buttons.current[tab.id] = element;
          }}
          id={`agent-model-tab-${tab.id}`}
          type="button"
          role="tab"
          aria-selected={value === tab.id}
          aria-controls={`agent-model-panel-${tab.id}`}
          tabIndex={value === tab.id ? 0 : -1}
          onClick={() => onChange(tab.id)}
          onKeyDown={(event) => handleKeyDown(event, tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}
